import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

/**
 * The three faces are split at the route boundary, so a visitor downloads one.
 *
 * Statically importing all three put every face — and therefore three.js, via
 * the showroom's `ProductStage` — into the entry chunk. The marketing homepage
 * then paid for the renderer and the operator workbench before painting a word
 * of copy, and deferring the hero *inside* the marketing page could not help:
 * the showroom's own static import had already pulled the renderer in.
 *
 * Splitting here is what makes the marketing page's deferred hero actually
 * defer. The renderer (~190 kB gzipped, larger than everything else combined)
 * is fetched when a stage mounts, not when the site loads.
 */
const App = lazy(async () => ({ default: (await import("./App")).App }));
const MarketingHome = lazy(async () => ({
  default: (await import("./marketing/MarketingHome")).MarketingHome,
}));
const Showroom = lazy(async () => ({
  default: (await import("./showroom/Showroom")).Showroom,
}));
const Showcase = lazy(async () => ({
  default: (await import("./showcase/Showcase")).Showcase,
}));

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root container.");

/**
 * One bundle, three faces. The marketing homepage is the front door; the
 * customer showroom is its proof, one click away, not the landing page
 * itself. The operator workbench stays behind an explicit flag so tooling
 * never greets a customer:
 *
 *   /                     → marketing homepage
 *   /?demo                → customer showroom
 *   /demo                 → customer showroom (needs a host rewrite; see below)
 *   /?demo&asset=luxury-watch → showroom, deep-linked to one piece
 *   /?showcase            → the asset library, every finish rendered live
 *   /?studio (any path)   → operator workbench (ingest, review, lighting)
 *   /?bench                → workbench in benchmark mode
 *
 * The showroom answers to BOTH a query flag and a path, and the query flag is
 * what the marketing CTAs link to. A path only resolves if the static host
 * rewrites unknown paths to index.html — and a host configured by hand in a
 * dashboard, rather than from render.yaml, does not necessarily do that. It
 * shipped as a 404 on `/demo` in production once; a query param cannot 404,
 * because the file being requested is always index.html.
 */
const params = new URLSearchParams(window.location.search);
const isWorkbench = params.has("studio") || params.has("operator") || params.has("bench");
const path = window.location.pathname.replace(/\/+$/, "") || "/";
const isShowroom = params.has("demo") || path === "/demo";
const isShowcase = params.has("showcase") || path === "/showcase";

function view(): React.JSX.Element {
  if (isWorkbench) return <App />;
  // Before the showroom: the showcase is a library index, and a visitor who
  // asked for it should not be handed a single product instead.
  if (isShowcase) return <Showcase />;
  if (isShowroom) return <Showroom />;
  return <MarketingHome />;
}

// The fallback is empty on purpose: the page background is already painted by
// styles.css, and every face opens with its own reveal — a spinner here would
// flash for one chunk fetch and then be replaced by a second intro animation.
createRoot(container).render(
  <StrictMode>
    <Suspense fallback={null}>{view()}</Suspense>
  </StrictMode>,
);
